import { useState } from "react";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, FileText, ShieldCheck } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import logoBlack from "@assets/TORK_Logo_Black_1779647609157.png";
import logoWhite from "@assets/TORK_Logo_White_1779647609157.png";

const REQUIRED_ACKS = [
  {
    id: "ack1",
    label: "I have read and agree to the Terms of Service and Liability Waiver.",
  },
  {
    id: "ack2",
    label:
      "I understand that Google Business Profiles and local SEO services carry inherent risks, including suspension, reverification, ranking loss, listing removal, and platform enforcement actions.",
  },
  {
    id: "ack3",
    label:
      "I understand that Tork – Digital Domination makes NO guarantees regarding rankings, leads, verification outcomes, reinstatements, visibility, revenue, or business performance.",
  },
  {
    id: "ack4",
    label:
      "I voluntarily assume all risks associated with the use of this Service and release Tork – Digital Domination from liability related to Google or third-party platform actions.",
  },
] as const;

export default function AcceptTermsPage() {
  const [checks, setChecks] = useState<Record<string, boolean>>({
    ack1: false,
    ack2: false,
    ack3: false,
    ack4: false,
  });
  const [, setLocation] = useLocation();
  const { logout } = useAuth();
  const { toast } = useToast();

  const allChecked = REQUIRED_ACKS.every((a) => checks[a.id]);

  const acceptMutation = useMutation({
    mutationFn: async () => {
      // Send the exact ack IDs + label text the user ticked so the
      // server-side audit log has a snapshot of what they agreed to.
      const ackedIds = REQUIRED_ACKS.filter((a) => checks[a.id]).map((a) => a.id);
      const ackedTexts = REQUIRED_ACKS.filter((a) => checks[a.id]).map((a) => a.label);
      const res = await apiRequest("POST", "/api/me/accept-tos", {
        acknowledgments: ackedIds,
        acknowledgmentTexts: ackedTexts,
      });
      return res.json();
    },
    onSuccess: async () => {
      // Await the refetch BEFORE navigating. Otherwise the router still
      // sees the stale user.tosAccepted=false and immediately bounces us
      // back to /accept-terms, forcing the user to click Accept twice.
      await queryClient.refetchQueries({ queryKey: ["/api/user"] });
      setLocation("/dashboard");
    },
    onError: () => {
      toast({ title: "Could not save your acceptance", description: "Please try again.", variant: "destructive" });
    },
  });

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4 py-10" data-testid="page-accept-terms">
      <div className="w-full max-w-2xl">
        <div className="flex justify-center mb-6">
          <img src={logoBlack} alt="Tork" className="h-16 block dark:hidden" />
          <img src={logoWhite} alt="Tork" className="h-16 hidden dark:block" />
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-lg">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-[#F6642A]/10 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-[#F6642A]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" data-testid="text-accept-title">Before you continue</h1>
              <p className="text-sm text-muted-foreground">
                Please review and acknowledge the following before using Tork – Digital Domination.
              </p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-5">
            Read the full{" "}
            <a href="/terms" target="_blank" rel="noreferrer" className="text-primary font-medium hover:underline" data-testid="link-terms">
              Terms of Service &amp; Liability Waiver
            </a>{" "}
            and{" "}
            <a href="/privacy" target="_blank" rel="noreferrer" className="text-primary font-medium hover:underline" data-testid="link-privacy">
              Privacy Policy
            </a>.
          </p>

          <div className="space-y-2.5">
            {REQUIRED_ACKS.map((ack, idx) => (
              <label
                key={ack.id}
                className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 cursor-pointer hover:bg-muted/60 transition-colors"
                data-testid={`label-${ack.id}`}
              >
                <Checkbox
                  checked={!!checks[ack.id]}
                  onCheckedChange={(v) => setChecks((prev) => ({ ...prev, [ack.id]: !!v }))}
                  className="mt-0.5"
                  data-testid={`checkbox-${ack.id}`}
                />
                <span className="text-sm leading-relaxed">
                  <span className="text-muted-foreground font-mono text-xs mr-2">#{idx + 1}</span>
                  {ack.label}
                </span>
              </label>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button
              className="flex-1 bg-[#F6642A] hover:bg-[#F6642A]/90 text-white"
              disabled={!allChecked || acceptMutation.isPending}
              onClick={() => acceptMutation.mutate()}
              data-testid="button-accept-continue"
            >
              {acceptMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
              Accept and continue
            </Button>
            <Button
              variant="ghost"
              onClick={() => logout.mutate()}
              data-testid="button-decline-signout"
            >
              Sign out
            </Button>
          </div>

          {!allChecked && (
            <p className="text-xs text-muted-foreground text-center mt-4" data-testid="text-acks-hint">
              All four acknowledgments are required to continue.
            </p>
          )}
        </div>

        <p className="text-xs text-muted-foreground text-center mt-4">
          Your electronic acceptance is recorded with a timestamp, IP address, and browser for legal records.
        </p>
      </div>
    </div>
  );
}
